import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const PREFS_QUERY_KEY = ["/api/user-preferences"];

/**
 * Hook to get/set a single user preference, backed by the server.
 * All preferences are fetched in a single request and cached.
 *
 * @param key - The preference key (e.g. "my_tasks.default_tab")
 * @param defaultValue - Fallback when no preference is saved
 * @returns [value, setValue, isLoading]
 */
export function useUserPreference<T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void, boolean] {
  const queryClient = useQueryClient();

  // Fetch all preferences once (shared across all useUserPreference calls)
  const { data: allPrefs, isLoading } = useQuery<Record<string, any>>({
    queryKey: PREFS_QUERY_KEY,
    queryFn: async () => {
      const res = await fetch("/api/user-preferences", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch preferences");
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 min - prefs rarely change externally
  });

  const mutation = useMutation({
    mutationFn: async (value: T) => {
      const res = await fetch(`/api/user-preferences/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error("Failed to save preference");
      return res.json();
    },
    // Optimistic update
    onMutate: async (newValue: T) => {
      await queryClient.cancelQueries({ queryKey: PREFS_QUERY_KEY });
      const previous = queryClient.getQueryData<Record<string, any>>(PREFS_QUERY_KEY);
      queryClient.setQueryData<Record<string, any>>(PREFS_QUERY_KEY, (old) => ({
        ...old,
        [key]: newValue,
      }));
      return { previous };
    },
    onError: (_err, _newValue, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREFS_QUERY_KEY, context.previous);
      }
    },
  });

  const value = (allPrefs?.[key] as T) ?? defaultValue;

  const setValue = useCallback(
    (newValue: T) => {
      mutation.mutate(newValue);
    },
    [mutation],
  );

  return [value, setValue, isLoading];
}
